/**
 * This file contains the Guest class and all the necessary functions to create a host and do the functionality of the Guests.
 */

/**
 * Class Guest
 *
 * @type {Object}
 */

import { PREFIX } from './constants';

import getID from './getID';

/**
 * Class Guest
 */
class Guest {

	/**
	 * Constructor
	 *
	 * @param {string} hostUri
	 */
	constructor( hostUri ) {
		this.storage = createGuest( hostUri );
	}

}

export default Guest;

/**
 * Creating a random ID based on Prefix and Data.
 *
 * @return {string} Returns a random generated string ID.
 */
const createId = () => {
	return PREFIX + Date.now();
};

/**
 * This function is responsible for creating a guest and carrying out all the functionalities of the guest.
 *
 * @param {object} source The source object
 * @param {object} parent The parent object
 * @return {function} It returns functions like set,get,remove, and close.
 */
function createGuest( source, parent ) {
	parent = parent || document.body;

	let contentWindow = null;
	const callbacks = {};
	const sessionRequests = [];
	let connected = false;
	let closed = true;
	let connectedTimeout = null;
	let isLoaded = false;

	/**
	 * Creating the iframe
	 *
	 * @param {string} [id='cross-domain-iframe']
	 * @return {Node} Returns a DOM Node.
	 */
	function createIframe( id = 'cross-domain-iframe' ) {
		const iframe = document.createElement( 'iframe' );
		iframe.src = source;
		iframe.width = 0;
		iframe.height = 0;
		iframe.style.display = 'none';
		iframe.id = id;
		iframe.onload = () => {
			isLoaded = true;
		};
		return iframe;
	}

	const iframe = createIframe();

	/**
	 * This function adds the iframe to the parent body and also adds an Event Listener.
	 *
	 */
	function connectHost() {
		parent.appendChild( iframe );
		contentWindow = iframe.contentWindow;
		closed = false;

		window.addEventListener( 'message', handleMessage );

		checkConnected(); // Checking if the connection has been established successfully between guests and host.
	}

	connectHost();

	/**
	 * This function handles the messages received via postMessage.
	 *
	 * @param {object} event The postMessage event object.
	 * @return {void}
	 */
	function handleMessage( event ) {
		const response = event.data;
		const sessionAccessId = getID( response );

		if ( 'sessionAccessId-connected' === sessionAccessId ) {
			connected = true;
			return;
		}

		if ( response.connectError ) {
			Object.keys( callbacks ).forEach( ( key ) => callbacks[key]( response.error ) );

			// Instead of reassigning, delete all properties while keeping the reference intact
			Object.keys( callbacks ).forEach( ( key ) => delete callbacks[key] );			

			return;
		}

		const callback = callbacks[sessionAccessId];

		if ( sessionAccessId && callback ) {
			callback( response.error, response.data );
		}
	}

	/**
	 * This function closes the connection.
	 *
	 */
	function close() {
		clearTimeout( connectedTimeout );
		window.removeEventListener( 'message', handleMessage );
		iframe.parentNode.removeChild( iframe );
		connected = false;
		closed = true;
	}

	/**
	 * This function is responsible for sending postMessage to the iframe/host.
	 *
	 * @param {string} method The method type.
	 * @param {string} key The cookie key.
	 * @param {string} value The cookie value.
	 * @param {function} callback The callback to be executed.
	 */
	function sendMessage( method, key, value, callback ) {
		if ( closed ) {
			connectHost();
		}

		if ( ! connected && 'connect' !== method ) {
			sessionRequests.push( [ method, key, value, callback ] );
		}

		const id = createId();

		if ( callbacks && 'function' === typeof callback ) {
			callbacks[id] = callback;
		}

		if ( isLoaded ) {
			contentWindow.postMessage(
				{
					method,
					key,
					value,
					id
				},
				source
			);
		}
	}

	/**
	 * Fetches a cookie from the iframe's First Party Cookie
	 *
	 * @param {string} key The cookie key
	 * @param {function} callback The Callback to be executed.
	 */
	function get( key, callback ) {
		if ( ! callback ) {
			throw new Error( 'callback required for GET' );
		}

		sendMessage( 'get', key, null, callback );
	}

	/**
	 * Sets a cookie value with a key to the iframe's First Party Cookie
	 *
	 * @param {string} key The cookie key
	 * @param {string} value The cookie value
	 * @param {function} callback The Callback to be executed.
	 */
	function set( key, value, callback ) {
		sendMessage( 'set', key, value, callback );
	}

	/**
	 * Removes a cookie with a key from the iframe's First Party Cookie
	 *
	 * @param {string} key The cookie key
	 * @param {function} callback The Callback to be executed.
	 */
	function remove( key, callback ) {
		sendMessage( 'remove', key, null, callback );
	}

	/**
	 * This function checks if the connection has been established or not.
	 *
	 * @return {void}
	 */
	function checkConnected() {
		if ( connected ) {
			clearTimeout( connectedTimeout );
			while ( sessionRequests.length ) {
				sendMessage( ...sessionRequests.pop() );
			}

			return;
		}

		sendMessage( 'connect' );

		connectedTimeout = setTimeout( checkConnected, 125 );
	}

	return {
		get,
		set,
		remove,
		close
	};
};
